import { useState } from 'react';

interface Props {
  name: string;
  input: unknown;
  output?: string | undefined;
  /** When true, tool call is still running (no result yet). */
  running: boolean;
  /** Visual position within a consecutive tool-call group. */
  position?: 'solo' | 'first' | 'middle' | 'last';
}

/**
 * Compact single-line tool row: "▶ tool_name · input-preview  chevron". Click
 * to expand an IN/OUT detail section. Rows are stacked inside a ToolGroup with
 * flush seams so a run of tool calls reads as one contiguous trace block.
 */
export function ToolOutputCard({ name, input, output, running, position = 'solo' }: Props) {
  const [expanded, setExpanded] = useState(false);

  const inputPreview = formatInputPreview(input);
  const verb = toolVerb(name);
  const displayName = shortenToolName(name);
  const hasOutput = typeof output === 'string' && output.length > 0;

  const isTop = position === 'solo' || position === 'first';
  const isBottom = position === 'solo' || position === 'last';

  return (
    <div
      style={{
        background: expanded ? 'var(--bg-alt2)' : 'transparent',
        borderTop: isTop ? 'none' : '1px solid var(--border-soft, var(--border))',
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: 12,
        color: 'var(--text-soft)',
        transition: 'background 120ms ease',
        borderBottomLeftRadius: isBottom ? 6 : 0,
        borderBottomRightRadius: isBottom ? 6 : 0,
        borderTopLeftRadius: isTop ? 6 : 0,
        borderTopRightRadius: isTop ? 6 : 0,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'transparent',
          border: 0,
          color: 'inherit',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          textAlign: 'left',
          minWidth: 0,
        }}
        aria-expanded={expanded}
      >
        <span
          aria-hidden
          style={{
            color: 'var(--text-mute)',
            fontSize: 10,
            flexShrink: 0,
            transition: 'transform 120ms ease',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-block',
            width: 8,
            textAlign: 'center',
          }}
        >
          ▸
        </span>
        <span
          style={{
            color: 'var(--accent)',
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {verb}
        </span>
        <span
          style={{
            color: 'var(--text-mute)',
            flexShrink: 0,
            fontWeight: 400,
          }}
        >
          {displayName}
        </span>
        {inputPreview && (
          <span
            style={{
              color: 'var(--text-mute)',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: 0.8,
            }}
          >
            · {inputPreview}
          </span>
        )}
        {running && (
          <span
            aria-label="Running"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--accent)',
              animation: 'sd-pulse 1.2s ease-in-out infinite',
              flexShrink: 0,
            }}
          />
        )}
        {!running && (
          <span
            aria-hidden
            style={{
              color: 'var(--text-mute)',
              fontSize: 10,
              flexShrink: 0,
              opacity: 0.5,
            }}
          >
            ✓
          </span>
        )}
      </button>
      {expanded && (
        <div style={{ padding: '0 10px 10px 28px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Section label="INPUT">
            <Pre>{prettyJson(input)}</Pre>
          </Section>
          {hasOutput ? (
            <Section label="OUTPUT">
              <Pre>{output}</Pre>
            </Section>
          ) : running ? (
            <Section label="OUTPUT">
              <span style={{ color: 'var(--text-mute)', fontStyle: 'italic', fontWeight: 400 }}>
                Waiting for result…
              </span>
            </Section>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontFamily: 'inherit',
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre
      style={{
        margin: 0,
        padding: '6px 8px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        fontSize: 11,
        fontFamily: 'inherit',
        color: 'var(--text)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: 240,
        overflow: 'auto',
      }}
    >
      {children}
    </pre>
  );
}

function prettyJson(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function formatInputPreview(input: unknown): string {
  if (input === null || input === undefined) return '';
  if (typeof input === 'string') return input;
  if (typeof input === 'object' && input !== null) {
    const obj = input as Record<string, unknown>;
    for (const key of ['command', 'file_path', 'path', 'url', 'pattern', 'query', 'prompt', 'description']) {
      const v = obj[key];
      if (typeof v === 'string' && v) return v;
    }
    try {
      const s = JSON.stringify(obj);
      return s.length > 140 ? s.slice(0, 137) + '…' : s;
    } catch {
      return '';
    }
  }
  return String(input);
}

/** Strip `mcp__namespace__` prefixes and Claude SDK tool prefixes so names read cleanly. */
function shortenToolName(name: string): string {
  const parts = name.split('__').filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1]! : name;
}

/** Lowercase past-tense verb for common tools (matches v1 ghost trace). */
function toolVerb(name: string): string {
  const short = shortenToolName(name);
  switch (short) {
    case 'Bash':
    case 'bash':
      return 'ran';
    case 'Task':
    case 'task':
      return 'delegated';
    case 'Glob':
    case 'glob':
      return 'globbed';
    case 'Read':
    case 'read':
      return 'read';
    case 'Write':
    case 'write':
      return 'wrote';
    case 'Edit':
    case 'edit':
      return 'edited';
    case 'Grep':
    case 'grep':
      return 'searched';
    case 'WebSearch':
    case 'web_search':
      return 'searched web';
    case 'WebFetch':
    case 'web_fetch':
      return 'fetched';
    case 'MultiEdit':
    case 'multi_edit':
      return 'edited';
    default:
      return 'called';
  }
}
